import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { statsAPI, authAPI } from '../../services/api';
import { DetailedStats } from '../../types';
import './AdminDashboard.css';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DetailedStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }

    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await statsAPI.getDetailedStats();
      setStats(response.data.stats);
    } catch (error) {
      console.error('İstatistik yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Backend'e logout isteği gönder
      await authAPI.logout();
    } catch (error) {
      console.error('Logout hatası:', error);
    } finally {
      // Her durumda frontend'i temizle
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <h2>
            <i className="bi bi-shield-lock-fill me-2"></i>
            Admin Panel
          </h2>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">
            <i className="bi bi-person-circle"></i>
          </div>
          <div className="user-info">
            <div className="user-name">{user?.name} {user?.surname}</div>
            <div className="user-role">{user?.role?.role_name}</div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <Link to="/admin" className="nav-item active">
            <i className="bi bi-speedometer2"></i>
            Dashboard
          </Link>
          <Link to="/admin/announcements" className="nav-item">
            <i className="bi bi-megaphone"></i>
            Duyurular
          </Link>
          {/* <Link to="/admin/gallery" className="nav-item">
            <i className="bi bi-images"></i>
            Galeri
          </Link> */}
          <Link to="/admin/faqs" className="nav-item">
            <i className="bi bi-question-circle"></i>
            SSS
          </Link>
          <Link to="/admin/users" className="nav-item">
            <i className="bi bi-people"></i>
            Kullanıcılar
          </Link>
        </nav>

        <div className="sidebar-footer">
          <Link to="/" className="btn btn-secondary w-100 mb-2">
            <i className="bi bi-house-door me-2"></i>
            Ana Sayfa
          </Link>
          <button onClick={handleLogout} className="btn btn-danger w-100">
            <i className="bi bi-box-arrow-right me-2"></i>
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-header">
          <h1>Dashboard</h1>
          <p className="text-muted">Hoş geldiniz, {user?.name}!</p>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card stat-card-primary">
            <div className="stat-icon">
              <i className="bi bi-people-fill"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats?.users.total_users || 0}</div>
              <div className="stat-label">Toplam Kullanıcı</div>
            </div>
          </div>

          <div className="stat-card stat-card-success">
            <div className="stat-icon">
              <i className="bi bi-person-check-fill"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats?.users.active_users || 0}</div>
              <div className="stat-label">Aktif Kullanıcı</div>
            </div>
          </div>

          <div className="stat-card stat-card-warning">
            <div className="stat-icon">
              <i className="bi bi-clock-history"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats?.today?.total_visits || 0}</div>
              <div className="stat-label">Gün İçi Giriş</div>
            </div>
          </div>

          <div className="stat-card stat-card-info">
            <div className="stat-icon">
              <i className="bi bi-graph-up-arrow"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats?.total_visits || 0}</div>
              <div className="stat-label">Toplam Site Ziyareti</div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="dashboard-row">
          {/* Son 7 Gün */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3>
                <i className="bi bi-graph-up me-2"></i>
                Son 7 Gün Ziyaretçi İstatistikleri
              </h3>
            </div>
            <div className="card-body">
              {stats?.last_7_days && stats.last_7_days.length > 0 ? (
                <div className="chart-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Tarih</th>
                        <th>Toplam Ziyaret</th>
                        <th>Benzersiz Ziyaretçi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.last_7_days.map((day, index) => (
                        <tr key={index}>
                          <td>{new Date(day.date).toLocaleDateString('tr-TR')}</td>
                          <td>{day.total_visits}</td>
                          <td>{day.unique_visitors}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted">Henüz veri yok</p>
              )}
            </div>
          </div>

          {/* En Çok Ziyaret Edilen Sayfalar */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3>
                <i className="bi bi-bar-chart me-2"></i>
                En Çok Ziyaret Edilen Sayfalar
              </h3>
            </div>
            <div className="card-body">
              {stats?.top_pages && stats.top_pages.length > 0 ? (
                <div className="page-list">
                  {stats.top_pages.slice(0, 5).map((page, index) => (
                    <div key={index} className="page-item">
                      <div className="page-info">
                        <div className="page-name">{page.visited_page}</div>
                        <div className="page-count">{page.visit_count} ziyaret</div>
                      </div>
                      <div className="page-bar">
                        <div
                          className="page-bar-fill"
                          style={{
                            width: `${(page.visit_count / (stats.top_pages[0]?.visit_count || 1)) * 100}%`
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">Henüz veri yok</p>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>
              <i className="bi bi-lightning-fill me-2"></i>
              Hızlı İşlemler
            </h3>
          </div>
          <div className="card-body">
            <div className="quick-actions">
              <Link to="/admin/announcements" className="action-btn action-btn-primary">
                <i className="bi bi-megaphone-fill"></i>
                <span>Yeni Duyuru</span>
              </Link>
              <Link to="/admin/gallery" className="action-btn action-btn-success">
                <i className="bi bi-image-fill"></i>
                <span>Resim Yükle</span>
              </Link>
              <Link to="/admin/faqs" className="action-btn action-btn-info">
                <i className="bi bi-question-circle-fill"></i>
                <span>SSS Ekle</span>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
